/**
 * @file     PriceBandsBlock.tsx
 * @purpose  Renders a content-engine price_bands block as a horizontal distribution of proportional bars.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ContentBlock } from '@/services/content-engine/types';

type PriceBandsBlockProps = Extract<ContentBlock, { t: 'price_bands' }>;

export const PriceBandsBlock = ({ caption, rows }: PriceBandsBlockProps) => {
  const { theme } = useAppTheme();
  const { t, i18n } = useTranslation();

  if (rows.length === 0) return null;

  const max = rows.reduce((acc, row) => Math.max(acc, row.count), 0);
  const total = rows.reduce((acc, row) => acc + row.count, 0);
  const formatCount = (n: number) => new Intl.NumberFormat(i18n.language || 'en-CA').format(n);

  return (
    <Box component="section">
      {caption ? (
        <Typography
          component="h2"
          sx={{
            fontFamily: theme.typography.fontFamilyHeading,
            fontSize: theme.typography.bodyLg,
            fontWeight: 600,
            color: theme.semanticColors.textPrimary,
            mb: '4px',
          }}
        >
          {caption}
        </Typography>
      ) : null}

      <Typography sx={{ fontSize: theme.typography.caption, color: theme.semanticColors.textMuted, mb: '18px' }}>
        {t('blog.blocks.priceBands.total', {
          // i18next treats `count` as the plural selector and interpolates it
          // raw, so 6287 printed unseparated. Pass the formatted string too.
          defaultValue: '{{formattedCount}} active listings',
          count: total,
          formattedCount: formatCount(total),
        })}
      </Typography>

      <Box sx={{ display: 'grid', gap: '2px' }}>
        {rows.map((row) => {
          const pct = max > 0 ? Math.max(row.count / max, row.count > 0 ? 0.02 : 0) : 0;
          return (
            <Box
              key={row.label}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '108px 1fr 52px', sm: '160px 1fr 64px' },
                alignItems: 'center',
                gap: { xs: '10px', sm: '16px' },
                minHeight: 44,
                py: '6px',
                borderBottom: `1px solid ${theme.semanticColors.borderSubtle}`,
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.bodySm,
                  color: theme.semanticColors.textSecondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.label}
              </Typography>

              <Box
                role="presentation"
                sx={{
                  height: 12,
                  borderRadius: `${theme.layout.radius.pill}px`,
                  backgroundColor: alpha(theme.rawSemanticColors.borderSubtle, 0.55),
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${(pct * 100).toFixed(2)}%`,
                    borderRadius: `${theme.layout.radius.pill}px`,
                    backgroundColor: theme.semanticColors.interactivePrimary,
                    transition: 'width 320ms ease',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamilyHeading,
                  fontSize: theme.typography.bodySm,
                  fontWeight: 600,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  color: theme.semanticColors.textPrimary,
                }}
              >
                {formatCount(row.count)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
